import { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Loader2, Mail, Pause, Play, Trash2 } from "lucide-react";
import { Pill } from "@/components/Music";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useConfirm } from "@/components/Confirm";
import { api, apiError, formatDate } from "@/lib/api";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

export function LoginStatus({ login }) {
  if (!login) return <Pill>No login</Pill>;
  if (login.suspended) return <Pill tone="bad">Suspended</Pill>;
  return <Pill tone="good">Can sign in</Pill>;
}

/** Asks for an optional private reason, then suspends. */
export function SuspendDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.user_id}/suspend`, { reason });
      toast.success(`${target.name || target.email} can't sign in until you restore them`);
      setReason("");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Suspend {target?.name || target?.email}?</DialogTitle>
        </DialogHeader>
        <ul className="text-[13px] text-muted2 space-y-1 list-disc pl-5">
          <li>They're signed out straight away and can't sign back in.</li>
          <li>Nothing else changes — songs, splits and payouts stay as they are.</li>
          <li>You can restore the login at any time with the same password.</li>
        </ul>
        <label className="block">
          <span className="block text-[12px] font-semibold mb-1.5">Reason (optional)</span>
          <input className={inputCls} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Contract under review" />
          <span className="block text-[11.5px] text-muted2 mt-1">Only your team sees this. They're never shown it.</span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={go} disabled={busy} className="bg-stock-out hover:bg-stock-out/90 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suspend login"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetLinkDialog({ link, onClose }) {
  return (
    <Dialog open={!!link} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Reset link for {link?.email}</DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-muted2">
          Send them this link. It works once and expires in {link?.expires_in_minutes} minutes.
          {link?.suspended && " Their login is suspended, so they can set a password but can't sign in until you restore it."}
        </p>
        <div className="flex gap-2">
          <input readOnly value={link?.link || ""} className={`${inputCls} font-mono text-[12px]`} aria-label="Reset link" />
          <Button
            variant="outline"
            aria-label="Copy link"
            onClick={() => {
              navigator.clipboard?.writeText(link.link);
              toast.success("Copied");
            }}
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export async function restoreLogin(target, onDone) {
  try {
    await api.post(`/admin/users/${target.user_id}/restore`);
    toast.success(`${target.name || target.email} can sign in again`);
    onDone?.();
  } catch (err) {
    toast.error(apiError(err));
  }
}

export async function fetchResetLink(target) {
  try {
    const { data } = await api.post(`/admin/users/${target.user_id}/reset-link`);
    return { ...data, suspended: !!target.suspended };
  } catch (err) {
    toast.error(apiError(err));
    return null;
  }
}

/** The "Writer login" box on a writer's sheet, for a writer who already has a login. */
export function WriterLoginBox({ writer, onChanged }) {
  const confirm = useConfirm();
  const login = writer.login;
  const target = { ...login, name: writer.name };
  const [suspending, setSuspending] = useState(null);
  const [link, setLink] = useState(null);
  const [emailEdit, setEmailEdit] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const changeEmail = async (e) => {
    e.preventDefault();
    const a = emailEdit.a.trim().toLowerCase();
    if (a !== emailEdit.b.trim().toLowerCase()) return toast.error("The two emails don't match");
    setEmailBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${login.user_id}/email`, { email: a });
      toast.success(`${writer.name} signs in with ${data.email} now — same password. Let them know.`);
      setEmailEdit(null);
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEmailBusy(false);
    }
  };

  const remove = async () => {
    if (
      !(await confirm({
        title: `Remove ${writer.name}'s login?`,
        body: "The login is deleted for good. Their writer record, songs and balances stay. To just block access for now, suspend instead.",
        confirmText: "Remove login",
        destructive: true,
      }))
    )
      return;
    try {
      await api.delete(`/admin/users/${login.user_id}`);
      toast.success("Login removed");
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-medium truncate">{login.email}</div>
          <div className="text-[12px] text-muted2">{login.last_login_at ? `Last signed in ${formatDate(login.last_login_at)}` : "Hasn't signed in yet"}</div>
        </div>
        <LoginStatus login={login} />
      </div>

      {login.suspended && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px]">
          <div className="font-semibold text-stock-out">Suspended{login.suspended_at ? ` on ${formatDate(login.suspended_at)}` : ""}</div>
          {login.suspend_reason && <div className="text-ink mt-0.5">Reason: {login.suspend_reason}</div>}
          <div className="text-muted2 mt-0.5">They see “Your login is suspended” and can't get in. Royalties keep adding to their balance.</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {login.suspended ? (
          <Button variant="outline" onClick={() => restoreLogin(target, onChanged)}>
            <Play className="w-4 h-4 mr-2" /> Restore login
          </Button>
        ) : (
          <Button variant="outline" onClick={() => setSuspending(target)} className="text-stock-out">
            <Pause className="w-4 h-4 mr-2" /> Suspend login
          </Button>
        )}
        <Button
          variant="outline"
          onClick={async () => {
            const l = await fetchResetLink(target);
            if (l) {
              setLink(l);
              onChanged();
            }
          }}
        >
          <KeyRound className="w-4 h-4 mr-2" /> Reset password
        </Button>
      </div>
      {emailEdit ? (
        <form onSubmit={changeEmail} className="rounded-xl border border-brand p-3 space-y-2">
          <div className="text-[13px] font-semibold">Change {writer.name}'s login email</div>
          <div className="text-[12px] text-muted2">They'll sign in with the new email from now on. Their password stays the same.</div>
          <input className="w-full h-9 rounded-lg border border-line bg-card px-3 text-[13px]" type="email" required placeholder="New login email" value={emailEdit.a} onChange={(e) => setEmailEdit({ ...emailEdit, a: e.target.value })} aria-label="New login email" />
          <input className="w-full h-9 rounded-lg border border-line bg-card px-3 text-[13px]" type="email" required placeholder="New login email again" value={emailEdit.b} onChange={(e) => setEmailEdit({ ...emailEdit, b: e.target.value })} aria-label="New login email again" />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={emailBusy} className="bg-brand hover:bg-brand-hover">
              Change login email
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setEmailEdit(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" className="w-full" onClick={() => setEmailEdit({ a: "", b: "" })}>
          <Mail className="w-4 h-4 mr-2" /> Change login email
        </Button>
      )}
      <button type="button" onClick={remove} className="text-[12px] text-muted2 hover:text-stock-out inline-flex items-center gap-1.5">
        <Trash2 className="w-3.5 h-3.5" /> Remove login
      </button>

      {login.login_log?.length > 0 && (
        <div className="border-t border-line pt-2.5">
          <div className="text-[11.5px] font-semibold text-muted2 uppercase tracking-wide mb-1.5">Activity</div>
          <ul className="space-y-1">
            {login.login_log.slice(0, 6).map((e, i) => (
              <li key={i} className="text-[12px] flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  {e.action}
                  {e.by ? <span className="text-muted2"> · {e.by}</span> : null}
                  {e.note ? <span className="text-muted2"> — {e.note}</span> : null}
                </span>
                <span className="text-muted2 whitespace-nowrap">{formatDate(e.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SuspendDialog target={suspending} onClose={() => setSuspending(null)} onDone={onChanged} />
      <ResetLinkDialog link={link} onClose={() => setLink(null)} />
    </div>
  );
}
